import React from 'react';
import {useTranslation} from 'react-i18next';
import {useHistory, useLocation} from 'react-router-dom';
import {useForm, FormProvider} from 'react-hook-form';
import {PATHS} from '../../../Routes';
import {NONE_EXEMPTION_OPTION} from '../../../utils/constants';
import {useComputedReservationDetails} from '../../../context/computedReservationDetails';
import {usePaymentSettings, useRegisterSteps} from '../../../hooks';
import {getNumberOfGuests} from 'utils/reservation';
import {useErrorModal, useIsMounted, useStatus} from 'utils/hooks';
import {useTaxes} from '../useTaxes';
import TouristTaxesCard from '../TouristTaxesCard';
import {
  CardTitle,
  CardWrapper,
  Content,
  ListGuestTaxes,
  NextButton,
  SubmitButtonWrapper,
  TotalColumn,
  TotalPriceBlock,
  TotalPriceTitle,
} from './styled';

export enum FORM_NAMES {
  guestsTaxes = 'guestsTaxes',
}

export enum FORM_NAMES_GUEST_TAX {
  age = 'age',
  exemption = 'exemption',
  total = 'total',
}

export type FormTypes = {
  [FORM_NAMES.guestsTaxes]: {
    [FORM_NAMES_GUEST_TAX.age]: string | number;
    [FORM_NAMES_GUEST_TAX.exemption]: {label: string; value: string};
    [FORM_NAMES_GUEST_TAX.total]: number;
  }[];
};

type FormTypesWithTotal = FormTypes & {total: number};
export type LocationStateTaxes = null | {
  formData?: {[key: number]: FormTypesWithTotal};
  guestsTaxes?: FormTypes['guestsTaxes'];
};

function TouristTaxesSetup() {
  const {t} = useTranslation();
  const history = useHistory();
  const isMounted = useIsMounted();
  const location = useLocation<LocationStateTaxes>();
  const {ErrorModal, displayError} = useErrorModal();
  const {reservation, hasTaxes, isReservationLoaded} = useComputedReservationDetails();
  const guestsCount = getNumberOfGuests(reservation);
  const {paymentSettingsCurrencySign: currency} = usePaymentSettings();
  const {isLoading, setStatus} = useStatus();
  const {count, getCurrentStep} = useRegisterSteps();
  const methods = useForm<FormTypes>({
    defaultValues: {
      [FORM_NAMES.guestsTaxes]: [
        {
          [FORM_NAMES_GUEST_TAX.age]: '',
          [FORM_NAMES_GUEST_TAX.exemption]: undefined,
          [FORM_NAMES_GUEST_TAX.total]: 0,
        },
      ],
    },
    mode: 'onChange',
  });
  const {
    reset,
    watch,
    handleSubmit,
    getValues,
    setValue,
    formState: {isValid},
  } = methods;
  const guestsTaxesWatch = watch(FORM_NAMES.guestsTaxes);

  const {
    guestsTaxes,
    calculateAndSetGuestTax,
    calculateAllGuestTax,
    isLoading: isSubmitting,
    availableTaxExemptionsOptions,
  } = useTaxes({
    onError: displayError,
  });

  React.useEffect(
    function redirectFromTaxes() {
      if (!hasTaxes && isReservationLoaded && !isSubmitting) {
        history.push(PATHS.pay, location.state);
      }
    },
    [hasTaxes, history, isReservationLoaded, isSubmitting, location.state],
  );

  React.useEffect(
    function preloadTaxes() {
      if (guestsTaxes) {
        const guestsTaxesList = guestsTaxes.guest_list?.map(tax => {
          const exemptionOption = availableTaxExemptionsOptions.find(
            option => option.value === tax.exemption,
          );

          return {
            [FORM_NAMES_GUEST_TAX.age]: tax.age,
            [FORM_NAMES_GUEST_TAX.exemption]: exemptionOption,
            [FORM_NAMES_GUEST_TAX.total]: tax.total,
          };
        });

        reset({[FORM_NAMES.guestsTaxes]: guestsTaxesList});
      }
    },
    [availableTaxExemptionsOptions, guestsTaxes, reset],
  );

  const calculateGuestsTax = React.useCallback(async () => {
    const data = await calculateAllGuestTax(getValues() as FormTypes);

    if (!isMounted.current) return;

    const guestListWithoutExemptions = data?.guest_list.filter(
      guest => guest.exemption === NONE_EXEMPTION_OPTION.value,
    );
    const total = Number(data?.amount) / Number(guestListWithoutExemptions?.length);

    data?.guest_list?.forEach((guest, index) => {
      const itemTotalName =
        `${FORM_NAMES.guestsTaxes}.${index}.${FORM_NAMES_GUEST_TAX.total}` as const;
      const value = guest.exemption !== NONE_EXEMPTION_OPTION.value ? 0 : total || 0;
      // @ts-ignore
      setValue(itemTotalName, value);
    });
  }, [calculateAllGuestTax, getValues, isMounted, setValue]);

  const getTotalPrice = () => {
    return Object.values(guestsTaxesWatch || []).reduce(
      (sum, tax) => sum + (tax?.total || 0),
      0,
    );
  };
  const buttonLabel = isValid && getTotalPrice() ? t('go_to_payment') : t('continue');

  const taxesCardsMemo = React.useMemo(() => {
    return new Array(guestsCount).fill('').map((item, index) => {
      const guestTaxItemName = `${FORM_NAMES.guestsTaxes}.${index}` as const;
      const title = `${t('guest')} ${index + 1 < 10 ? `0${index + 1}` : `${index + 1}`}`;

      return (
        <CardWrapper key={guestTaxItemName}>
          <CardTitle>{title}</CardTitle>
          <TouristTaxesCard
            className="touristTaxesCard"
            index={index}
            guestItemName={guestTaxItemName}
            setTotalStatus={setStatus}
            taxExemptionsOptions={availableTaxExemptionsOptions}
            calculateGuestsTax={calculateGuestsTax}
            visibleTotal
          />
        </CardWrapper>
      );
    });
  }, [calculateGuestsTax, availableTaxExemptionsOptions, guestsCount, setStatus, t]);

  const handleNextClick = (data: FormTypes) => {
    calculateAndSetGuestTax(data, getTotalPrice());
  };

  return (
    <div>
      <FormProvider {...methods}>
        <Content>
          <ListGuestTaxes>{taxesCardsMemo}</ListGuestTaxes>
          <TotalColumn>
            <TotalPriceBlock>
              <TotalPriceTitle>
                <span>{t('total_taxes')}</span>
                <span>
                  {getTotalPrice().toFixed(2)}
                  {currency}
                </span>
              </TotalPriceTitle>
              <SubmitButtonWrapper>
                <div className="progress">{`${getCurrentStep(
                  PATHS.touristTaxes,
                )}/${count}`}</div>
                <NextButton
                  disabled={isLoading || isSubmitting}
                  label={buttonLabel}
                  onClick={handleSubmit(handleNextClick)}
                  iconRight={'shoppingBagIcon'}
                />
              </SubmitButtonWrapper>
            </TotalPriceBlock>
          </TotalColumn>
        </Content>
      </FormProvider>
      <ErrorModal />
    </div>
  );
}

export {TouristTaxesSetup};
